import { action, makeObservable, observable } from "mobx";
import { fetchStatus } from "utils/storesUtils";
import { withRequestTracking } from "utils/storesUtils";

type fetchStatusType = (typeof fetchStatus)[keyof typeof fetchStatus];
const defaultStateStatus: { loading: boolean; fetchStatus: fetchStatusType; fetchError: unknown } = {
  loading: false,
  fetchStatus: fetchStatus.idle,
  fetchError: undefined,
};

interface IDataStoreConstructorParams<T> {
  fetchMethod: () => Promise<T>;
  defaultData: T;
}

const POLLING_INTERVAL = 30000;

export class DataStoreConstructor<T> {
  data: T;
  statuses = { ...defaultStateStatus };
  fetchMethod: () => Promise<T>;
  defaultData: T;
  pollingInterval: number | null = POLLING_INTERVAL;

  private pollingId: ReturnType<typeof setInterval> | null = null;

  constructor(params: IDataStoreConstructorParams<T>) {
    this.fetchMethod = params.fetchMethod;
    this.data = params.defaultData;
    this.defaultData = params.defaultData;

    makeObservable(this, {
      data: observable,
      statuses: observable,
      fetch: action.bound,
    });
  }

  fetch = () => {
    return withRequestTracking(this.statuses, async () => {
      this.data = await this.fetchMethod();
    });
  };

  public polling = () => {
    if (this.pollingId) return;

    this.fetch();
    this.pollingId = setInterval(() => {
      this.fetch();
    }, this.pollingInterval || 0);
  };

  public stopPolling = () => {
    if (this.pollingId) {
      clearInterval(this.pollingId);
      this.pollingId = null;
    }
  };

  clear = () => {
    this.data = this.defaultData || ({} as T);
    this.statuses = { ...defaultStateStatus };
    this.stopPolling();
  };
}
